// src/LeaveRequestModal.js - VERSIONE FINALE E CORRETTA

import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';
import { createNotification } from './notificationService';

const processLeaveApproval = httpsCallable(functions, 'processLeaveApproval');

export function LeaveRequestModal({ onClose, personId, personName, selectedDate, leaveType, leaveHours: propLeaveHours, onSubmit, confirmText, effectivePermissionLevel, personProfileData }) {
    const { userProfile } = useUser();
    const [allStaff, setAllStaff] = useState([]);

    const actualLeavesPermission = effectivePermissionLevel || (userProfile.permissions?.leaves || 'none');
    const isManager = actualLeavesPermission !== 'self-edit';
    const [selectedUserId, setSelectedUserId] = useState(personId || userProfile.uid);

    useEffect(() => {
        // Aggiungi il controllo per userProfile e centerId
        if (isManager && userProfile && userProfile.centerId) { 
            const fetchStaff = async () => {
                // Usa il nuovo percorso nidificato
                const staffSnapshot = await getDocs(collection(db, 'centers', userProfile.centerId, 'staff'));
                const staffList = staffSnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    // --- FIX SICUREZZA QUI: Gestisce i nomi mancanti ---
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    // ---------------------------------------------------
                setAllStaff(staffList);
            };
            fetchStaff();
        } else {
            setAllStaff([userProfile]); 
        }
    }, [isManager, userProfile]);

    // Determina su quale profilo lavorare: quello passato (da manager) o il proprio
    const targetProfile = useMemo(() => {
        if (isManager) {
            // Cerca nella lista staff l'utente attualmente selezionato nel dropdown
            return allStaff.find(s => s.id === selectedUserId);
        }
        // Se non manager, il target sei sempre tu
        return userProfile; 
    }, [selectedUserId, isManager, allStaff, userProfile]);

    // 2. Deriva il flag 104 da questo profilo target reattivo
    const hasLaw104 = useMemo(() => 
        targetProfile?.leaveBalances?.hasLaw104 || false
    , [targetProfile]); // Si ri-esegue quando 'targetProfile' cambia

    // 3. Funzione per decidere il tipo iniziale (basata sulle PROPS iniziali)
    const getInitialType = (lt, profileData) => {
        const initialHas104 = (profileData || userProfile)?.leaveBalances?.hasLaw104 || false;
        if (lt === 'Permesso') {
            return initialHas104 ? 'Permesso ROL' : 'Permesso retribuito (ROL)';
        }
        return lt || 'Ferie';
    };

    const [type, setType] = useState(() => getInitialType(leaveType));
    const [startDate, setStartDate] = useState(() => moment(selectedDate).format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(() => moment(selectedDate).format('YYYY-MM-DD'));
    const [leaveHours, setLeaveHours] = useState(propLeaveHours || { h: 2, m: 0 });
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');
    const [editingRequest, setEditingRequest] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    
    useEffect(() => {
        const initialProfile = personProfileData || userProfile;
        
        setStartDate(moment(selectedDate).format('YYYY-MM-DD'));
        setEndDate(moment(selectedDate).format('YYYY-MM-DD'));
        setLeaveHours(propLeaveHours || { h: 2, m: 0 });
        setSelectedUserId(personId || userProfile.uid);
        
        // Imposta il tipo iniziale basandoti sul profilo passato dalle props
        setType(getInitialType(leaveType, initialProfile));
        
    }, [personId, personName, personProfileData, selectedDate, leaveType, propLeaveHours, userProfile]);

    // 5. useEffect per REAGIRE al cambio del dropdown (selectedUserId)
    // Questo è il pezzo che mancava.
    useEffect(() => {
        // 'hasLaw104' è già stato ricalcolato grazie a 'useMemo'
        
        const currentTypeIs104 = type === 'Permesso 104';
        const currentTypeIsROL = type === 'Permesso ROL';
        const currentTypeIsGenericROL = type === 'Permesso retribuito (ROL)';

        if (hasLaw104) {
            // Il NUOVO utente selezionato HA la 104.
            // Se il tipo attuale è il ROL generico, cambialo in "Permesso ROL".
            if (currentTypeIsGenericROL) {
                setType('Permesso ROL');
            }
        } else {
            // Il NUOVO utente selezionato NON HA la 104.
            // Se il tipo attuale era "Permesso 104" o "Permesso ROL",
            // forzalo a "Permesso retribuito (ROL)" (l'unico valido).
            if (currentTypeIs104 || currentTypeIsROL) {
                setType('Permesso retribuito (ROL)');
            }
        }
        
    }, [hasLaw104]);

    // Cerca se esiste già un'assenza approvata per questo giorno/utente
    useEffect(() => {
        const checkExisting = async () => {
            if (!userProfile?.centerId || !startDate) return;
            const targetId = isManager ? selectedUserId : (userProfile.uid || userProfile.id);
            if (!targetId) return;

            try {
                const dayStr = moment(startDate).format('YYYY-MM-DD');
                // Firestore non supporta range su 2 campi diversi,
                // quindi query per userId+status e filtro date client-side
                const q = query(
                    collection(db, 'centers', userProfile.centerId, 'leaveRequests'),
                    where('userId', '==', targetId),
                    where('status', '==', 'approved')
                );
                const snap = await getDocs(q);
                // Filtra client-side: cerca una request che copre questo giorno
                const matchingDocs = snap.docs.filter(d => {
                    const data = d.data();
                    const reqStart = moment(data.startDate).format('YYYY-MM-DD');
                    const reqEnd = moment(data.endDate).format('YYYY-MM-DD');
                    return dayStr >= reqStart && dayStr <= reqEnd;
                });
                if (matchingDocs.length > 0) {
                    const existing = { id: matchingDocs[0].id, ...matchingDocs[0].data() };
                    setEditingRequest(existing);
                    setIsEditMode(true);
                    setType(existing.type || 'Ferie');
                    setNotes(existing.notes || existing.note || '');
                    if (existing.leaveHours) {
                        setLeaveHours({ h: existing.leaveHours.h || 0, m: existing.leaveHours.m || 0 });
                    }
                    if (existing.startDate) setStartDate(moment(existing.startDate).format('YYYY-MM-DD'));
                    if (existing.endDate) setEndDate(moment(existing.endDate).format('YYYY-MM-DD'));
                } else {
                    setEditingRequest(null);
                    setIsEditMode(false);
                }
            } catch (err) {
                console.warn("Errore ricerca assenza esistente:", err);
            }
        };
        checkExisting();
    }, [startDate, selectedUserId, userProfile?.centerId]);

    const isHourlyLeave = ['Permesso ROL', 'Permesso 104', 'Permesso non retribuito', 'Permesso retribuito (ROL)', 'Permesso studio'].includes(type);

    const handleSubmit = async () => {
    setError('');
    // Controlli iniziali (invariati)
    if (!startDate || !endDate || (isManager && !selectedUserId)) {
      setError('Tutti i campi obbligatori devono essere compilati.');
      return;
    }
    if (moment(endDate).isBefore(moment(startDate))) {
      setError('La data di fine non può essere precedente a quella di inizio.');
      return;
    }

    // Se viene passata una funzione onSubmit esterna (come da ShiftsPage per i permessi), usala
    // ── EDIT MODE: modifica o elimina assenza esistente ──────────────
    if (isEditMode && editingRequest) {
      const isZeroHours = isHourlyLeave && leaveHours && leaveHours.h === 0 && (leaveHours.m || 0) === 0;
      
      if (isZeroHours) {
        // 0 ore = ELIMINA l'assenza
        if (!window.confirm("Stai impostando 0 ore. Vuoi eliminare questa assenza?")) return;
        try {
          const cancelFn = httpsCallable(functions, 'cancelLeaveRequest');
          await toast.promise(
            cancelFn({ requestId: editingRequest.id, centerId: userProfile.centerId }),
            { pending: 'Eliminazione assenza...', success: 'Assenza eliminata!', error: 'Errore eliminazione.' }
          );
          onClose();
        } catch (err) {
          setError("Errore: " + err.message);
        }
        return;
      }

      // MODIFICA l'assenza esistente
      try {
        const totalDays = moment(endDate).diff(moment(startDate), 'days') + 1;
        const updateFn = httpsCallable(functions, 'updateLeaveRequest');
        await toast.promise(
          updateFn({
            requestId: editingRequest.id,
            centerId: userProfile.centerId,
            updates: {
              type,
              startDate,
              endDate,
              totalDays,
              leaveHours: isHourlyLeave ? leaveHours : null,
              note: notes?.trim() || '',
            },
          }),
          { pending: 'Aggiornamento assenza...', success: 'Assenza aggiornata!', error: 'Errore aggiornamento.' }
        );
        onClose();
      } catch (err) {
        setError("Errore: " + err.message);
      }
      return;
    }

    // Se viene passata una funzione onSubmit esterna (come da ShiftsPage per i permessi), usala
    // (SOLO per nuove assenze, non per modifiche)
    if (onSubmit) {
      onSubmit({ type, startDate, endDate, notes, leave: isHourlyLeave ? leaveHours : null });
      return;
    }

    // --- INIZIO MODIFICA: Logica Status Richiesta ---
    const totalDays = moment(endDate).diff(moment(startDate), 'days') + 1;
    const isPastDate = moment(endDate).isBefore(moment(), 'day');
    let status = 'pending';
    
    const autoApprovedTypes = [
        'Permesso 104', 'Malattia', 'Infortunio',
        'Congedo paternità', 'Lutto familiare',
        'Congedo matrimoniale', 'Donazione sangue',
    ];
    
    if (autoApprovedTypes.includes(type) || isPastDate || isManager) {
        status = 'approved';
    }
    // Ferie, ROL, Congedo parentale, Permesso studio, Permesso non retribuito → 'pending' (richiedono approvazione)
    // --- FINE MODIFICA: Logica Status Richiesta ---

    const targetUser = isManager ? allStaff.find(s => s.id === selectedUserId) : userProfile;
    if (!targetUser) {
      setError("Profilo utente non trovato.");
      return;
    }

    // --- INIZIO NUOVA LOGICA: Recupero Original Shifts ---
    let originalShifts = {}; // Oggetto per conservare i turni originali
    try {
      const startMoment = moment(startDate);
      const endMoment = moment(endDate);
      const affectedMonths = new Set(); // Trova i mesi interessati dalla richiesta

      // Cicla sui giorni della richiesta per identificare i mesi
      for (let day = startMoment.clone(); day.isSameOrBefore(endMoment); day.add(1, 'day')) {
        affectedMonths.add(day.format("YYYY-MM"));
      }

      // Per ogni mese interessato, leggi il documento schedule corrispondente
      for (const monthId of affectedMonths) {
    // AGGIUNGI userProfile.centerId al percorso
    const scheduleDocRef = doc(db, 'centers', userProfile.centerId, 'schedules', monthId); // <-- MODIFICA QUI
        const scheduleDoc = await getDoc(scheduleDocRef); // Usa getDoc per leggere una volta sola

        if (scheduleDoc.exists()) {
          const scheduleData = JSON.parse(scheduleDoc.data().schedule || "{}");
          // Cicla nuovamente sui giorni della richiesta per quel mese
          for (let day = startMoment.clone(); day.isSameOrBefore(endMoment); day.add(1, 'day')) {
            if (day.format("YYYY-MM") === monthId) {
              const dayStr = day.format("YYYY-MM-DD");
              // Salva lo shift originale di quel giorno per l'utente target
              // Se non esiste, salva un oggetto vuoto {}
              originalShifts[dayStr] = scheduleData[dayStr]?.[targetUser.name] || {};
            }
          }
        }
      }
    } catch(err) {
      console.error("Errore nel recuperare gli originalShifts:", err);
      // Non bloccare l'operazione, ma segnala. Il ripristino potrebbe non essere perfetto.
    }
    // --- FINE NUOVA LOGICA: Recupero Original Shifts ---

    // Costruisci il payload della richiesta
    const requestPayload = {
      userId: targetUser.uid || targetUser.id, // Usa uid se disponibile (da auth), altrimenti id (da firestore)
      userName: targetUser.name,
      type: type,
      startDate: startDate,
      endDate: endDate,
      totalDays: totalDays,
      notes: notes.trim(),
      status: status, // Usa lo status calcolato
      requestedAt: new Date(),
      originalShifts: originalShifts, // *** MODIFICA: Aggiungi gli originalShifts recuperati ***
     centerId: userProfile.centerId,
    };

    // Aggiungi le ore specifiche se è un permesso orario
    if (isHourlyLeave) {
      requestPayload.leaveHours = leaveHours;
    }

   try {
        // ── NUOVA ASSENZA ───────────────────────────────────────────────
        const newRequestRef = await addDoc(
          collection(db, 'centers', userProfile.centerId, 'leaveRequests'),
          requestPayload
        );

        if (status === 'approved') {
          toast.promise(
            processLeaveApproval({ requestId: newRequestRef.id, centerId: userProfile.centerId }),
            {
              pending: 'Approvazione automatica in corso...',
              success: 'Calendario e contatori aggiornati!',
              error: 'Errore durante l\'approvazione automatica.',
            }
          );
          if (isManager && userProfile.uid !== (targetUser.uid || targetUser.id)) {
            createNotification(
              targetUser.uid || targetUser.id,
              userProfile,
              `ha inserito e approvato una richiesta di ${type} per te.`,
              '/assenze'
            );
          }
        } else if (status === 'pending') {
          const staffRef = collection(db, 'centers', userProfile.centerId, 'staff');
          const sq = query(staffRef, where("permissions.leaves", "in", ["completo", "semi-completo"]));
          const querySnapshot = await getDocs(sq);
          querySnapshot.forEach(d => {
            if (d.id !== (userProfile.uid || userProfile.id)) {
              createNotification(d.id, userProfile, `ha inviato una nuova richiesta di ${type}.`, '/assenze');
            }
          });
          createNotification(
            targetUser.uid || targetUser.id,
            { name: "Sistema", uid: "system" },
            `La tua richiesta di ${type} è stata inviata ed è in attesa di approvazione.`,
            '/assenze'
          );
        }

        toast.success("Richiesta inviata con successo!");
      onClose();

    } catch (err) { // Gestione errori
      console.error("Errore nell'invio della richiesta: ", err);
      setError('Si è verificato un errore. Riprova.');
    }
  };

    return (
        <div className="modal" onMouseDown={onClose}>
            <div className="modal-content" onMouseDown={e => e.stopPropagation()} style={{maxWidth: '500px'}}>
                {/* MODIFICA: Usa targetProfile.name per il titolo */}
                <h3>{isEditMode ? '✏️ Modifica Assenza' : 'Nuova Richiesta di Assenza'} per {personName || targetProfile?.name}</h3>

                <div style={{ textAlign: 'left', marginTop: '20px' }}>
                    {isEditMode && editingRequest && (
                        <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                            <p style={{ margin: 0, fontSize: '0.88rem', color: '#92400e', fontWeight: '600' }}>
                                ⚠️ Assenza già registrata: <b>{editingRequest.type}</b>
                                {editingRequest.leaveHours ? ` (${editingRequest.leaveHours.h}h${editingRequest.leaveHours.m ? ` ${editingRequest.leaveHours.m}m` : ''})` : ` (${editingRequest.totalDays} giorni)`}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                                Le modifiche sovrascriveranno l'assenza esistente.
                            </p>
                        </div>
                    )}
                    <div className="form-group" style={{marginBottom: '15px'}}>
                        <label>Tipo di Richiesta</label>
                        <select value={type} onChange={(e) => setType(e.target.value)}>
                            <optgroup label="Ferie e Permessi">
                                <option value="Ferie">Ferie</option>
                                {hasLaw104 ? (
                                    <>
                                        <option value="Permesso ROL">Permesso ROL</option>
                                        <option value="Permesso 104">Permesso L.104</option>
                                    </>
                                ) : (
                                    <option value="Permesso retribuito (ROL)">Permesso retribuito (ROL)</option>
                                )}
                                <option value="Permesso non retribuito">Permesso non retribuito</option>
                                <option value="Permesso studio">Permesso studio (150h/anno)</option>
                            </optgroup>
                            <optgroup label="Congedi">
                                <option value="Maternità">Maternità</option>
                                <option value="Congedo paternità">Congedo di paternità (10gg)</option>
                                <option value="Congedo parentale">Congedo parentale</option>
                                <option value="Congedo matrimoniale">Congedo matrimoniale (15gg)</option>
                            </optgroup>
                            <optgroup label="Assenze giustificate">
                                <option value="Malattia">Malattia</option>
                                <option value="Infortunio">Infortunio sul lavoro</option>
                                <option value="Lutto familiare">Lutto familiare (3gg)</option>
                                <option value="Donazione sangue">Donazione sangue (1gg)</option>
                            </optgroup>
                            {/* --- FINE NUOVA LOGICA --- */}
                        </select>
                    </div>
                    {isManager && (
                        <div className="form-group" style={{textAlign: 'left', marginTop: '10px'}}>
                            <label>Richiesta per:</label>
                            {/* Il valore di questo select è controllato da 'selectedUserId' */}
                            <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                                {allStaff.map(staff => (
                                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {isHourlyLeave ? (
                        <>
                            <div className="form-group">
                                <label>Giorno del permesso</label>
                                <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setEndDate(e.target.value); }} />
                            </div>
                            <div className="form-group" style={{marginTop: '15px'}}>
                                <label>Ore di permesso richieste (HH:MM)</label>
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                    <input type="number" value={leaveHours.h} onChange={e => setLeaveHours({...leaveHours, h: parseInt(e.target.value) || 0})} min="0" placeholder="HH" style={{width: '70px'}}/>
                                    <span>:</span>
                                    <input type="number" value={leaveHours.m} onChange={e => setLeaveHours({...leaveHours, m: parseInt(e.target.value) || 0})} min="0" step="15" placeholder="MM" style={{width: '70px'}}/>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="form-row">
                            <div className="form-group" style={{flex: 1}}>
                                <label>Dal Giorno</label>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>
                            <div className="form-group" style={{flex: 1}}>
                                <label>Al Giorno</label>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>
                        </div>
                    )}
                    <div className="form-group" style={{marginTop: '15px'}}>
                        <label>Note (opzionale)</label>
                        <textarea 
                            value={notes} 
                            onChange={(e) => setNotes(e.target.value)}
                            rows="3"
                            style={{width: '100%', boxSizing: 'border-box', resize: 'vertical'}}
                            placeholder="Aggiungi eventuali dettagli per il responsabile..."
                        ></textarea>
                    </div>
                </div>

                {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}

                <div style={{marginTop: '25px'}}>
                    <button onClick={handleSubmit} className="modal-button confirm-button">{isEditMode ? '✏️ Aggiorna Assenza' : (confirmText || 'Invia Richiesta')}</button>
                    <button onClick={onClose} className="modal-button cancel-button">Annulla</button>
                </div>
            </div>
        </div>
    );
}